import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type Marker } from '@air/component-player';
import { useShortIdContext } from '@air/provider-short-id';

import { useDiscussionPanelContextSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { discussionsFilterSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextUtils';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { getTimestampMarkers } from '~/components/v2/components/shared/video/getTimestampMarkers';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';
import { usePublicDiscussions } from '~/swr-hooks/discussions/usePublicDiscussions';
import { canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

export const usePublicVideoVisualizerClipMarkers = () => {
  const boardId = useURLBoardIdSelector();
  const clip = useSelector(centralizedClipSelector);
  const { shortId } = useShortIdContext();
  const { permissions } = usePublicPermissions();
  const discussionsFilter = useDiscussionPanelContextSelector(discussionsFilterSelector);

  const canViewDiscussions = canViewAssetDiscussions(permissions);

  const { data: discussions } = usePublicDiscussions({
    boardId,
    assetId: clip.assetId,
    clipId: clip.id,
    shortId,
    isEnabled: canViewDiscussions,
  });

  const markers = useMemo<Marker[]>(() => {
    if (!canViewDiscussions) {
      return [];
    }

    if (discussions) {
      return getTimestampMarkers({
        data: discussions,
        currentVersion: clip.version,
        filter: discussionsFilter,
      });
    }

    return [];
  }, [canViewDiscussions, clip.version, discussions, discussionsFilter]);

  return { markers };
};
